import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Users, 
  Trophy, 
  MapPin,
  Search,
  Filter,
  Globe,
  Mail,
  ChevronRight,
  Menu,
  X,
  ArrowLeft
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Navbar
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-heading text-2xl font-bold">
              <span className="text-primary">MICHEZO</span>
              <span className="text-secondary">NET</span>
            </span>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/events" className="text-white/70 hover:text-primary transition-colors font-body text-sm">Events</Link>
            <Link to="/directory" className="text-primary font-body text-sm">Directory</Link>
            <Link to="/login" className="btn-secondary flex items-center justify-center">Login</Link>
            <Link to="/signup" className="btn-primary flex items-center justify-center">Get Started</Link>
          </div>
          
          <button className="md:hidden text-white" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        
        {isOpen && (
          <div className="md:hidden pb-6 flex flex-col gap-4">
            <Link to="/events" className="text-white/70 py-2" onClick={() => setIsOpen(false)}>Events</Link>
            <Link to="/directory" className="text-primary py-2" onClick={() => setIsOpen(false)}>Directory</Link>
            <Link to="/login" className="btn-secondary w-full text-center" onClick={() => setIsOpen(false)}>Login</Link>
            <Link to="/signup" className="btn-primary w-full text-center" onClick={() => setIsOpen(false)}>Get Started</Link>
          </div>
        )}
      </div>
    </nav>
  );
};

const DirectoryPage = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  const orgTypes = ['Federation', 'Club', 'Academy', 'School', 'Facility', 'Corporate', 'Government'];
  const countries = ['Kenya', 'Nigeria', 'South Africa', 'Ghana', 'Tanzania', 'Uganda', 'Rwanda', 'Ethiopia'];
  
  const tabs = [
    { id: 'all', label: 'All', icon: Building2 },
    { id: 'federation', label: 'Federations', icon: Trophy },
    { id: 'club', label: 'Clubs', icon: Users },
    { id: 'academy', label: 'Academies', icon: Trophy },
    { id: 'facility', label: 'Facilities', icon: MapPin }
  ];

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get(`${API}/organizations`);
        setOrganizations(response.data);
      } catch (err) {
        // Placeholder data
        setOrganizations([
          { id: '1', name: 'Football Kenya Federation', type: 'Federation', sport: 'Football', country: 'Kenya', city: 'Nairobi', website: 'footballkenya.org', description: 'National governing body for football in Kenya' },
          { id: '2', name: 'Gor Mahia FC', type: 'Club', sport: 'Football', country: 'Kenya', city: 'Nairobi', description: 'Premier League football club' },
          { id: '3', name: 'Kenya Basketball Federation', type: 'Federation', sport: 'Basketball', country: 'Kenya', city: 'Nairobi', description: 'National basketball governing body' },
          { id: '4', name: 'Strathmore University Sports', type: 'Academy', sport: 'Multi-Sport', country: 'Kenya', city: 'Nairobi', description: 'University sports academy' },
          { id: '5', name: 'Kasarani Stadium', type: 'Facility', sport: 'Multi-Sport', country: 'Kenya', city: 'Nairobi', description: 'National stadium with 60,000 capacity' },
          { id: '6', name: 'Rugby Africa', type: 'Federation', sport: 'Rugby', country: 'Kenya', city: 'Nairobi', description: 'Continental rugby union' },
          { id: '7', name: 'AFC Leopards', type: 'Club', sport: 'Football', country: 'Kenya', city: 'Nairobi', description: 'Historic football club' },
          { id: '8', name: 'Kenya Rugby Union', type: 'Federation', sport: 'Rugby', country: 'Kenya', city: 'Nairobi', description: 'National rugby governing body' },
          { id: '9', name: 'Nyayo National Stadium', type: 'Facility', sport: 'Multi-Sport', country: 'Kenya', city: 'Nairobi', description: 'Multi-purpose stadium' }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchOrganizations();
  }, []);

  const filteredOrganizations = organizations.filter(org => {
    const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !typeFilter || org.type === typeFilter;
    const matchesCountry = !countryFilter || org.country === countryFilter;
    const matchesTab = activeTab === 'all' || org.type.toLowerCase() === activeTab;
    return matchesSearch && matchesType && matchesCountry && matchesTab;
  });

  const getOrgIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'federation': return Trophy;
      case 'club': return Users;
      case 'academy': return Trophy;
      case 'facility': return MapPin;
      default: return Building2;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-24 pb-12 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/5 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6" data-testid="back-home">
              <ArrowLeft size={18} />
              <span className="font-body text-sm">Back to Home</span>
            </Link>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Sports Industry Directory
            </h1>
            <p className="text-white/60 font-body max-w-2xl">
              Discover federations, clubs, academies, and sports facilities across Africa. 
              Connect with organizations and explore the sports ecosystem.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Tabs */}
      <section className="pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-body text-sm whitespace-nowrap transition-all ${
                  activeTab === tab.id 
                    ? 'bg-primary text-black' 
                    : 'bg-card border border-border text-white/70 hover:border-primary hover:text-primary'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Filters */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search organizations..."
                className="w-full bg-card border border-border focus:border-primary rounded-md h-12 pl-12 pr-4 text-white font-body outline-none"
                data-testid="directory-search"
              />
            </div>
            
            {/* Type Filter */}
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="bg-card border border-border focus:border-primary rounded-md h-12 pl-12 pr-8 text-white font-body outline-none appearance-none cursor-pointer min-w-[160px]"
                data-testid="directory-type-filter"
              >
                <option value="">All Types</option>
                {orgTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            {/* Country Filter */}
            <select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className="bg-card border border-border focus:border-primary rounded-md h-12 px-4 text-white font-body outline-none appearance-none cursor-pointer min-w-[160px]"
              data-testid="directory-country-filter"
            >
              <option value="">All Countries</option>
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>
        </div>
      </section>
      
      {/* Organizations Grid */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredOrganizations.length === 0 ? (
            <div className="text-center py-16">
              <Building2 className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/60 font-body">No organizations found matching your criteria</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredOrganizations.map((org, i) => {
                const OrgIcon = getOrgIcon(org.type);
                return (
                  <motion.div
                    key={org.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-card border border-border/40 rounded-xl p-6 card-hover group cursor-pointer"
                    data-testid={`org-card-${org.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                        <OrgIcon className="w-7 h-7 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-body text-secondary uppercase tracking-wider">{org.type}</span>
                        <h3 className="font-heading text-lg font-bold text-white mt-1 group-hover:text-primary transition-colors truncate">
                          {org.name}
                        </h3>
                        <p className="text-white/50 text-sm font-body mt-1">{org.sport}</p>
                      </div>
                    </div>
                    
                    {org.description && (
                      <p className="text-white/60 text-sm font-body mt-4 line-clamp-2">{org.description}</p>
                    )}
                    
                    <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                      <div className="flex items-center gap-4 text-white/50 text-sm font-body">
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {org.city}, {org.country}
                        </span>
                      </div>
                      {org.website && (
                        <a href={`https://${org.website}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          <Globe size={16} />
                        </a>
                      )}
                    </div>
                    
                    <button className="mt-4 w-full btn-secondary flex items-center justify-center gap-2 text-xs h-10">
                      View Profile <ChevronRight size={14} />
                    </button>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DirectoryPage;
